import { useState, useEffect, useCallback } from 'react';
import type { User } from 'firebase/auth';
import { useAuth } from './useAuth';
import { Release } from '../types/release';

// Module-scoped so concurrent hook instances share one request: the release
// bell is mounted twice in AppHeader (desktop + mobile variants), and each
// instance's mount effect was fetching the unread count separately.
let inFlightUnreadCount: Promise<number | null> | null = null;

const fetchUnreadCountShared = (user: User): Promise<number | null> => {
  if (!inFlightUnreadCount) {
    inFlightUnreadCount = (async () => {
      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/releases/unread`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          return data.count || 0;
        }
        return null;
      } catch (error) {
        console.error('Error fetching unread count:', error);
        return null;
      } finally {
        inFlightUnreadCount = null;
      }
    })();
  }
  return inFlightUnreadCount;
};

export const useReleases = () => {
  const { user } = useAuth();
  const [releases, setReleases] = useState<Release[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;

    const count = await fetchUnreadCountShared(user);
    if (count !== null) {
      setUnreadCount(count);
    }
  }, [user]);

  const fetchReleases = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/releases`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setReleases(data.releases || []);
      }
    } catch (error) {
      console.error('Error fetching releases:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const markAsViewed = useCallback(async () => {
    if (!user) return;

    try {
      const token = await user.getIdToken();
      await fetch(
        `${process.env.REACT_APP_API_URL}/api/releases/mark-viewed`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Reset unread count
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking releases as viewed:', error);
    }
  }, [user]);

  // Fetch unread count on mount and when user changes
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  return {
    releases,
    unreadCount,
    loading,
    fetchReleases,
    markAsViewed,
  };
};
